import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import { Code2, KeyRound, ArrowLeft, ArrowUpRight, Copy, Check, ShieldCheck, Zap, Lock } from 'lucide-react';

const Snippet = ({ id, code, lang = 'bash' }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) { /* ignore */ }
  };
  return (
    <div className="relative rounded-xl bg-charcoal text-white p-4 font-mono text-[12.5px] leading-relaxed overflow-x-auto" data-testid={`snippet-${id}`}>
      <button onClick={copy} className="absolute top-2 right-2 px-2 py-1 rounded-md bg-white/10 hover:bg-white/20 text-[11px] inline-flex items-center gap-1 transition-colors" data-testid={`snippet-copy-${id}`}>
        {copied ? <><Check className="w-3 h-3" /> Copié</> : <><Copy className="w-3 h-3" /> Copier</>}
      </button>
      <pre className="whitespace-pre"><code>{code}</code></pre>
      <span className="absolute bottom-1 left-3 text-[10px] text-white/40 uppercase tracking-wider">{lang}</span>
    </div>
  );
};

const Endpoint = ({ method, path, desc }) => {
  const colors = {
    GET: 'bg-secondary text-charcoal',
    POST: 'bg-primary text-charcoal',
    PATCH: 'bg-accent text-charcoal',
    DELETE: 'bg-danger text-white',
  };
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-charcoal/10 last:border-0">
      <span className={`text-[11px] font-bold tracking-wider rounded-md px-2 py-1 shrink-0 ${colors[method] || 'bg-surface-alt'}`}>{method}</span>
      <code className="font-mono text-[13px] text-charcoal break-all flex-1 pt-0.5">{path}</code>
      <span className="text-xs text-charcoal/60 hidden sm:block pt-0.5 text-right max-w-[40%]">{desc}</span>
    </div>
  );
};

const ApiDocs = () => {
  const apiBase = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';
  const swaggerUrl = `${apiBase}/docs`;

  const curlGenerate = `curl -X POST "${apiBase}/partners/v1/generate" \\
  -H "X-Partner-Api-Key: ptr_VOTRE_CLE_API" \\
  -H "Content-Type: application/json" \\
  -d '{
    "count": 50,
    "label_text": "Scannez-moi",
    "notes": "Lot janvier 2026"
  }'`;

  const curlVerify = `curl "${apiBase}/pingmes/TGR-XXXX-XXXX-XXXX-XXXX/public"`;

  const curlAuth = `curl -X POST "${apiBase}/auth/login" \\
  -H "Content-Type: application/json" \\
  -d '{"email": "vous@exemple.com", "password": "••••••••"}'`;

  return (
    <div className="min-h-screen bg-background" data-testid="apidocs-page">
      <Header />
      <main className="pb-20">
        {/* Hero */}
        <header className="border-b-2 border-charcoal/10 bg-surface-alt/40">
          <div className="max-w-4xl mx-auto px-6 md:px-8 py-12 md:py-16">
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-charcoal/60 hover:text-charcoal mb-6" data-testid="apidocs-back">
              <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
            </Link>
            <p className="text-xs tracking-[0.25em] uppercase text-charcoal/60 mb-3">Documentation API · v1</p>
            <h1 className="font-heading font-bold text-4xl md:text-5xl text-charcoal tracking-tight mb-4">
              Intégrez Tagora à votre stack
            </h1>
            <p className="text-charcoal/70 text-base md:text-lg leading-relaxed max-w-2xl">
              API REST complète pour émettre, vérifier et transférer des Tagora&nbsp;ID à l'échelle.
              Authentification par clé API, réponses JSON, exemples prêts à copier.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <a href={swaggerUrl} target="_blank" rel="noopener noreferrer" className="btn-outline inline-flex items-center justify-center gap-2" data-testid="apidocs-swagger-link">
                <Code2 className="w-4 h-4" /> Console interactive (Swagger) <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
              <Link to="/business#contact" className="btn-primary inline-flex items-center justify-center gap-2" data-testid="apidocs-contact-link">
                Obtenir une clé API
              </Link>
            </div>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-6 md:px-8 py-12 space-y-12">
          {/* Highlights */}
          <section className="grid sm:grid-cols-3 gap-4">
            {[
              { Icon: Zap, title: 'Génération en bulk', desc: 'Créez jusqu\'à 10 000 IDs par appel.' },
              { Icon: ShieldCheck, title: 'Vérification offline', desc: 'Checksum cryptographique embarqué.' },
              { Icon: Lock, title: 'Anonymat de bout en bout', desc: 'Aucune donnée personnelle exposée.' },
            ].map(({ Icon, title, desc }) => (
              <div key={title} className="card-brutal bg-white p-5">
                <Icon className="w-5 h-5 text-primary mb-2" />
                <p className="font-bold text-charcoal mb-1">{title}</p>
                <p className="text-sm text-charcoal/70 leading-relaxed">{desc}</p>
              </div>
            ))}
          </section>

          {/* Authentication */}
          <section data-testid="apidocs-auth">
            <p className="text-xs tracking-[0.25em] uppercase text-charcoal/60 mb-3">01 — Authentification</p>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-charcoal mb-4 inline-flex items-center gap-3"><KeyRound className="w-6 h-6 text-primary" /> Clé API partenaire</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4 text-sm md:text-base">
              Les appels à l'API partenaire s'authentifient via un en-tête <code className="bg-surface-alt px-1.5 py-0.5 rounded text-[13px]">X-Partner-Api-Key</code>.
              Votre clé est disponible dans votre dashboard partenaire après inscription.
            </p>
            <Snippet id="auth-header" code={`X-Partner-Api-Key: ptr_VOTRE_CLE_API`} lang="header" />
            <p className="text-charcoal/70 text-sm mt-4">
              Pour les actions côté utilisateur final (compte personnel), utilisez l'authentification JWT classique&nbsp;:
            </p>
            <div className="mt-3"><Snippet id="auth-login" code={curlAuth} /></div>
          </section>

          {/* Quick start */}
          <section data-testid="apidocs-quickstart">
            <p className="text-xs tracking-[0.25em] uppercase text-charcoal/60 mb-3">02 — Démarrage rapide</p>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-charcoal mb-4">Générer 50 Tagora&nbsp;IDs</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4 text-sm md:text-base">
              Un seul appel suffit pour générer un lot. Chaque ID retourné inclut son QR code en base64
              et son URL de vérification publique.
            </p>
            <Snippet id="generate" code={curlGenerate} />
          </section>

          {/* Public verification */}
          <section data-testid="apidocs-verify">
            <p className="text-xs tracking-[0.25em] uppercase text-charcoal/60 mb-3">03 — Vérification publique</p>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-charcoal mb-4">Lire le statut d'un Tagora&nbsp;ID</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4 text-sm md:text-base">
              N'importe quel terminal peut interroger l'annuaire public pour vérifier l'existence,
              le statut (actif, perdu, transféré) et l'organisation émettrice d'un Tagora&nbsp;ID.
              Aucune authentification requise.
            </p>
            <Snippet id="verify" code={curlVerify} />
          </section>

          {/* Endpoint list */}
          <section data-testid="apidocs-endpoints">
            <p className="text-xs tracking-[0.25em] uppercase text-charcoal/60 mb-3">04 — Endpoints principaux</p>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-charcoal mb-4">Référence rapide</h2>
            <div className="card-brutal bg-white p-5">
              <p className="text-xs font-bold tracking-widest uppercase text-charcoal/60 mb-3">Partenaires (clé API)</p>
              <Endpoint method="POST"   path="/api/partners/v1/generate" desc="Générer un lot de Tagora IDs" />
              <Endpoint method="GET"    path="/api/partners/v1/ids" desc="Lister les IDs émis" />
              <Endpoint method="GET"    path="/api/partners/v1/batches" desc="Lister les lots" />
              <Endpoint method="GET"    path="/api/partners/v1/export.csv" desc="Exporter en CSV" />
              <Endpoint method="GET"    path="/api/partners/v1/export.zip" desc="Exporter les QR en ZIP" />
            </div>
            <div className="card-brutal bg-white p-5 mt-4">
              <p className="text-xs font-bold tracking-widest uppercase text-charcoal/60 mb-3">Public &amp; vérification</p>
              <Endpoint method="GET"    path="/api/pingmes/{id}/public" desc="Vérifier publiquement un ID" />
              <Endpoint method="GET"    path="/api/short/{code}" desc="Résoudre un code court (mini-QR)" />
              <Endpoint method="POST"   path="/api/messages/anonymous" desc="Envoyer un message anonyme" />
            </div>
            <div className="card-brutal bg-white p-5 mt-4">
              <p className="text-xs font-bold tracking-widest uppercase text-charcoal/60 mb-3">Compte utilisateur (JWT)</p>
              <Endpoint method="POST"   path="/api/auth/register" desc="Créer un compte" />
              <Endpoint method="POST"   path="/api/auth/login" desc="Se connecter" />
              <Endpoint method="POST"   path="/api/pingmes" desc="Créer un Tagora ID personnel" />
              <Endpoint method="GET"    path="/api/pingmes" desc="Lister mes IDs" />
              <Endpoint method="PATCH"  path="/api/pingmes/{id}" desc="Modifier un ID" />
              <Endpoint method="DELETE" path="/api/pingmes/{id}" desc="Supprimer un ID" />
            </div>
            <p className="text-xs text-charcoal/60 mt-4">
              La <Link to="/" onClick={(e) => { e.preventDefault(); window.open(swaggerUrl, '_blank'); }} className="text-primary underline">console interactive Swagger</Link> liste l'intégralité des 155&nbsp;endpoints.
            </p>
          </section>

          {/* Rate limits & errors */}
          <section data-testid="apidocs-limits">
            <p className="text-xs tracking-[0.25em] uppercase text-charcoal/60 mb-3">05 — Limites &amp; erreurs</p>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-charcoal mb-4">Conventions</h2>
            <ul className="list-disc pl-5 space-y-2 text-charcoal/80 leading-relaxed text-sm md:text-base">
              <li>Toutes les requêtes/réponses utilisent JSON UTF-8.</li>
              <li>Codes HTTP standards&nbsp;: <code className="bg-surface-alt px-1 rounded">200</code> succès, <code className="bg-surface-alt px-1 rounded">400</code> requête invalide, <code className="bg-surface-alt px-1 rounded">401</code> non authentifié, <code className="bg-surface-alt px-1 rounded">403</code> droits insuffisants, <code className="bg-surface-alt px-1 rounded">404</code> introuvable.</li>
              <li>Génération en lot&nbsp;: maximum <strong>10 000 IDs</strong> par appel <code className="bg-surface-alt px-1 rounded">/partners/v1/generate</code>.</li>
              <li>Les erreurs retournent un objet <code className="bg-surface-alt px-1 rounded">&#123;"detail": "message"&#125;</code> en français par défaut.</li>
            </ul>
          </section>

          {/* CTA */}
          <section className="card-brutal bg-secondary/30 p-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div>
              <p className="font-bold text-charcoal mb-1">Prêt à intégrer&nbsp;?</p>
              <p className="text-sm text-charcoal/70">Contactez-nous pour obtenir votre clé API partenaire et un accompagnement technique.</p>
            </div>
            <Link to="/business#contact" className="btn-primary inline-flex items-center justify-center gap-2 shrink-0" data-testid="apidocs-cta-contact">
              Obtenir un accès <ArrowUpRight className="w-4 h-4" />
            </Link>
          </section>
        </div>
      </main>
    </div>
  );
};

export default ApiDocs;
